import { Link, useParams } from "react-router-dom";

import { groupRules } from "@/lib/groupMembership";
import { groupName, groupHref, membersCountText, canSeeInternal } from "@/lib/v3Groups";
import { useMemberAuth } from "../MemberAuthContext";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { SearchField, FilterBar, Pill, ResultTable, ResultRow } from "../components/v3app";
import { useGroup, GroupFrame, GroupAside, GroupNoAccess } from "../components/v3groups";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", ...(iso.length === 10 ? { timeZone: "UTC" } : {}) });

// Loads the circle from GET /member/groups; members-only views render M120 without a confirmed membership.
function WithGroup({ membersOnly, children }) {
  const { id } = useParams();
  const { res, group } = useGroup(id);
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (res.data === null) return <Loading />;
  if (!group || (membersOnly && !canSeeInternal(group))) return <GroupNoAccess g={group} />;
  return children(group);
}

// M70: released rules from the circle object (rules, rules_version, rules_updated_at); readable before joining, reading is no consent.
export function GroupRules() {
  return <WithGroup>{(g) => {
    const r = groupRules(g);
    return (
      <div data-m="M70" data-testid="group-rules">
        <V3Title title="Kreisregeln lesen" intro={`Die Regeln gelten für den Kreis ${groupName(g)}.`} testid="group-rules-title" />
        <V3Columns layout="app" aside={<GroupAside text={r ? r.items[0] : groupName(g)} />} main={
          <GroupFrame g={g} tab="regeln">
            {r ? <>
              <DetailList>{r.items.map((t, i) => <DetailRow key={i} label={`Regel ${i + 1}`} value={t} testid={`group-rules-item-${i}`} />)}</DetailList>
              <p className="mt-[12px] text-[13px] text-v3-muted" data-testid="group-rules-version">Fassung {r.version} · Stand {dayLabel(r.updatedAt)} · Das Lesen gilt nicht als Zustimmung.</p>
            </> : <Unavailable testid="group-rules-unavailable">Noch keine freigegebene Fassung verfügbar. Es werden keine Regeln angezeigt oder erfunden.</Unavailable>}
            <V3Actions>
              <Link to={groupHref(g)} data-testid="group-rules-preview" className={V3_PRIMARY}>Zur Kreisvorschau</Link>
              <Link to="/kreise" data-testid="group-rules-back" className={V3_SECONDARY}>Zurück</Link>
            </V3Actions>
          </GroupFrame>} />
      </div>
    );
  }}</WithGroup>;
}

// M72: only the confirmed count exists; the named list needs GET /member/groups/{id}/members (D-53).
export function GroupMembers() {
  return <WithGroup membersOnly>{(g) => (
    <div data-m="M72" data-testid="group-members">
      <V3Title title="Mitglieder meines Kreises" intro="Diese Mitgliederliste gehört zu deinem aufgenommenen Kreis." testid="group-members-title" />
      <SearchField id="group-members-q" label="Mitglieder durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value="" onChange={() => {}} disabled describedBy="group-members-unavailable" testid="group-members-search" />
      <div className="mt-[17px]"><ResultTable testid="group-members-table" label="Mitglieder">
        <ResultRow initial="B" title="Bestätigte Mitglieder" context={membersCountText(g)} action="" testid="group-members-count" />
        <ResultRow initial="N" title="Namensliste" context="Noch nicht verfügbar" action="" testid="group-members-list" />
        <ResultRow initial="K" title="Kreisverantwortung" context="Nicht angegeben" action="" testid="group-members-responsible" />
      </ResultTable></div>
      <div className="mt-[14px]"><Unavailable id="group-members-unavailable" testid="group-members-unavailable">Die Mitglieder mit Namen liefert der gemeinsame Core noch nicht. Es werden keine Personen angezeigt oder erraten.</Unavailable></div>
      <V3Actions className="mt-[21px]">
        <button type="button" disabled aria-describedby="group-members-unavailable" data-testid="group-members-select" className={V3_PRIMARY}>Mitglied auswählen</button>
        <Link to={groupHref(g)} data-testid="group-members-back" className={V3_SECONDARY}>Zur Kreisvorschau</Link>
      </V3Actions>
    </div>
  )}</WithGroup>;
}

// M73: activities carry no circle in Core 1.1; meetings need GET /member/groups/{id}/meetings (D-52).
export function GroupMeetings() {
  const { member } = useMemberAuth();
  return <WithGroup membersOnly>{(g) => (
    <div data-m="M73" data-testid="group-meetings">
      <V3Title title="Treffen meines Kreises" intro="Treffen, die deinem aufgenommenen Kreis zugeordnet sind." testid="group-meetings-title" />
      <SearchField id="group-meetings-q" label="Treffen durchsuchen" placeholder="Aktivität oder öffentlichen Ort suchen …" value="" onChange={() => {}} disabled describedBy="group-meetings-unavailable" testid="group-meetings-search" />
      <FilterBar testid="group-meetings-filters">
        {member?.city && <Pill tone="ink">{member.city}</Pill>}
        <Pill tone="ink">Zugangsangaben</Pill>
      </FilterBar>
      <div className="mt-[16px]"><Unavailable id="group-meetings-unavailable" testid="group-meetings-unavailable">Treffen dieses Kreises liefert der gemeinsame Core noch nicht; Aktivitäten sind keinem Kreis zugeordnet. Es werden keine Termine angezeigt.</Unavailable></div>
      <V3Actions className="mt-[21px]">
        <Link to="/aktivitaeten" data-testid="group-meetings-activities-link" className={V3_PRIMARY}>Offene Aktivitäten ansehen</Link>
        <Link to={groupHref(g)} data-testid="group-meetings-back" className={V3_SECONDARY}>Zur Kreisvorschau</Link>
      </V3Actions>
    </div>
  )}</WithGroup>;
}

export { WithGroup };
